import { useEffect, useRef } from "react";
import {
  CHAT_PENDING_TRIGGERS_CLEARED,
  enqueueFirstWinsChatTrigger,
  scheduleTrackedChatTriggerDelay,
} from "@/hooks/chat/helpers/chatTriggerCoordinator";

/** Pixels from true bottom to count as "reached bottom" (toolbar / subpixel). */
const NEAR_BOTTOM_PX = 80;
/** Treat as top-of-page for zero / bounce / iOS rounding. */
const NEAR_TOP_PX = 2;

export const useReturnToTopTrigger = ({
  enabled,
  timeout = 5,
  popModal,
  triggerChat,
  triggerKey,
  sessionEpoch = 0,
}) => {
  /** User must scroll to the bottom first; halfway is not enough. */
  const hasReachedBottom = useRef(false);
  const hasTimeoutStarted = useRef(false);
  const hasTriggered = useRef(false);
  const cancelCountdownRef = useRef(null);

  useEffect(() => {
    hasReachedBottom.current = false;
    hasTimeoutStarted.current = false;
    hasTriggered.current = false;
    if (cancelCountdownRef.current) {
      cancelCountdownRef.current();
      cancelCountdownRef.current = null;
    }
  }, [sessionEpoch]);

  useEffect(() => {
    const onPendingCleared = () => {
      hasTimeoutStarted.current = false;
      cancelCountdownRef.current = null;
    };
    document.addEventListener(CHAT_PENDING_TRIGGERS_CLEARED, onPendingCleared);
    return () =>
      document.removeEventListener(
        CHAT_PENDING_TRIGGERS_CLEARED,
        onPendingCleared,
      );
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (hasTriggered.current) return;

      const scrollPosition = window.scrollY;
      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );

      if (maxScroll > 0 && scrollPosition >= maxScroll - NEAR_BOTTOM_PX) {
        hasReachedBottom.current = true;
      }

      if (hasReachedBottom.current && scrollPosition <= NEAR_TOP_PX) {
        if (hasTimeoutStarted.current) return;
        hasTimeoutStarted.current = true;
        if (cancelCountdownRef.current) cancelCountdownRef.current();
        cancelCountdownRef.current = scheduleTrackedChatTriggerDelay(
          triggerKey,
          "Scroll Up",
          timeout,
          () => {
            enqueueFirstWinsChatTrigger(() => {
              if (hasTriggered.current) return;
              const ran = triggerChat("Scrolled Back To Top", {
                popModal,
                triggerKey,
                ...(!enabled && { isProxyEvent: true }),
              });
              if (ran !== false) {
                hasTriggered.current = true;
              }
            });
            cancelCountdownRef.current = null;
          },
        );
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (cancelCountdownRef.current) cancelCountdownRef.current();
    };
  }, [enabled, timeout, popModal, triggerChat, triggerKey, sessionEpoch]);
};
